import React, { useState, useEffect } from 'react';
import { RiCloseLine } from 'react-icons/ri';
import Button from '@/components/common/Button/Button';
import Input from '@/components/forms/Input/Input';
import Spinner from '@/components/common/Spinner/Spinner';
import { type AdminSize } from '@/hooks/admin/useAdmin';
import { useCreateSize, useUpdateSize } from '@/hooks/admin/useAdminSizes';

interface SizeModalProps {
    isOpen: boolean;
    onClose: () => void;
    size?: AdminSize;
    onSuccess?: () => void;
}

const SizeModal: React.FC<SizeModalProps> = ({
    isOpen,
    onClose,
    size,
    onSuccess
}) => {
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        additional_cost: 0,
        sort_order: 0,
        is_active: true
    });

    const createSize = useCreateSize();
    const updateSize = useUpdateSize();

    const isEditing = !!size;
    const isLoading = createSize.isPending || updateSize.isPending;

    useEffect(() => {
        if (size) {
            setFormData({
                name: size.name,
                description: size.description || '',
                additional_cost: size.additional_cost,
                sort_order: size.sort_order,
                is_active: size.is_active
            });
        } else {
            setFormData({
                name: '',
                description: '',
                additional_cost: 0,
                sort_order: 0,
                is_active: true
            });
        }
    }, [size, isOpen]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.name.trim()) {
            alert('El nombre del tamaño es requerido');
            return;
        }

        if (formData.additional_cost < 0) {
            alert('El costo adicional debe ser mayor o igual a cero');
            return;
        }

        try {
            if (isEditing && size) {
                await updateSize.mutateAsync({
                    id: size.id,
                    data: formData
                });
            } else {
                await createSize.mutateAsync(formData);
            }

            onSuccess?.();
            onClose();
        } catch (error) {
            console.error('Error saving size:', error);
            alert('Error al guardar el tamaño');
        }
    };

    if (!isOpen) return null;

    return (
        <div className="admin-modal-overlay">
            <div className="admin-modal-content">
                <div className="admin-modal-header">
                    <h2 className="admin-modal-title">
                        {isEditing ? 'Editar Tamaño' : 'Nuevo Tamaño'}
                    </h2>
                    <button
                        onClick={onClose}
                        className="admin-modal-close"
                        disabled={isLoading}
                    >
                        <RiCloseLine />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="admin-modal-form">
                    <div className="admin-form-grid">
                        <div className="admin-form-group">
                            <Input
                                label="Nombre"
                                name="name"
                                value={formData.name}
                                setValue={(value) => setFormData(prev => ({ ...prev, name: value }))}
                                placeholder="Ingrese el nombre del tamaño"
                                required
                            />
                        </div>

                        <div className="admin-form-group">
                            <Input
                                label="Costo Adicional"
                                name="additional_cost"
                                type="number"
                                step="0.01"
                                value={formData.additional_cost.toString()}
                                setValue={(value) => setFormData(prev => ({ ...prev, additional_cost: parseFloat(value) || 0 }))}
                                placeholder="0.00"
                            />
                        </div>

                        <div className="admin-form-group">
                            <Input
                                label="Orden de Visualización"
                                name="sort_order"
                                type="number"
                                value={formData.sort_order.toString()}
                                setValue={(value) => setFormData(prev => ({ ...prev, sort_order: parseInt(value) || 0 }))}
                                placeholder="0"
                            />
                        </div>

                        <div className="admin-form-group admin-form-full">
                            <Input
                                label="Descripción"
                                name="description"
                                value={formData.description}
                                setValue={(value) => setFormData(prev => ({ ...prev, description: value }))}
                                placeholder="Descripción del tamaño (opcional)"
                                rows={3}
                            />
                        </div>

                        <div className="admin-form-group">
                            <label className="admin-checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={formData.is_active}
                                    onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                                />
                                <span>Tamaño Activo</span>
                            </label>
                        </div>
                    </div>

                    <div className="admin-modal-actions">
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={onClose}
                            disabled={isLoading}
                        >
                            Cancelar
                        </Button>
                        <Button
                            type="submit"
                            variant="primary"
                            disabled={isLoading}
                        >
                            {isLoading ? <Spinner size="sm" /> : null}
                            {isEditing ? 'Actualizar' : 'Crear'} Tamaño
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default SizeModal;
